import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAccounts, useBatchItems, useMe, useRef } from '../api/hooks';
import { useFormDraft } from '../api/drafts';
import { ChatWidget } from '../components/ChatWidget';
import { RefSelect } from '../components/RefSelect';
import type { ItemInput } from '../api/types';

interface ItemForm {
  name: string;
  sku: string;
  type: string;
  description: string;
  unitPrice: string;
  purchaseCost: string;
  incomeAccountId: string;
  expenseAccountId: string;
  categoryId: string;
}

const empty = (): ItemForm => ({
  name: '', sku: '', type: 'Service', description: '',
  unitPrice: '', purchaseCost: '', incomeAccountId: '', expenseAccountId: '', categoryId: '',
});

export function NewItemPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const batch = useBatchItems();
  const me = useMe();
  const income = useAccounts('Income');
  const expense = useAccounts('Expense');
  const categories = useRef('categories');

  const [rows, setRows] = useState<ItemForm[]>([empty()]);
  const setRow = (i: number, patch: Partial<ItemForm>) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, empty()]);
  const removeRow = (i: number) => setRows((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));

  // Server-side autosave draft (one live item draft per user; holds the whole multi-row form).
  const { discard, savedAt } = useFormDraft('item', { rows }, (p) => {
    const d = p as { rows?: ItemForm[] };
    if (Array.isArray(d.rows) && d.rows.length) setRows(d.rows.map((r) => ({ ...empty(), ...r })));
  });
  const clearDraft = () => {
    void discard();
    qc.invalidateQueries({ queryKey: ['drafts'] });
  };

  // Assistant fills the first empty (unnamed) row, otherwise appends a new one.
  const applyExtract = (x: Record<string, unknown>) => {
    setRows((rs) => {
      const idx = rs.findIndex((r) => !r.name.trim());
      const base = idx >= 0 ? rs[idx] : empty();
      const merged: ItemForm = {
        ...base,
        name: typeof x.name === 'string' ? x.name : base.name,
        sku: typeof x.sku === 'string' ? x.sku : base.sku,
        description: typeof x.description === 'string' ? x.description : base.description,
        unitPrice: x.unitPrice != null ? String(x.unitPrice) : base.unitPrice,
        purchaseCost: x.purchaseCost != null ? String(x.purchaseCost) : base.purchaseCost,
      };
      if (idx >= 0) return rs.map((r, i) => (i === idx ? merged : r));
      return [...rs, merged];
    });
  };

  const named = rows.filter((r) => r.name.trim());
  const canSubmit = named.length > 0 && !batch.isPending;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const items: ItemInput[] = named.map((r) => ({
      name: r.name.trim(),
      sku: r.sku.trim() || undefined,
      type: r.type === 'NonInventory' ? 'NonInventory' : 'Service',
      description: r.description.trim() || undefined,
      unitPrice: r.unitPrice ? Number(r.unitPrice) : undefined,
      purchaseCost: r.purchaseCost ? Number(r.purchaseCost) : undefined,
      incomeAccountId: r.incomeAccountId || undefined,
      expenseAccountId: r.expenseAccountId || undefined,
      categoryId: r.categoryId || undefined,
    }));
    batch.mutate(items, { onSuccess: () => { clearDraft(); navigate('/items'); } });
  };

  return (
    <>
      <h1>Add Items / Services</h1>
      <ChatWidget
        formType="item"
        enabled={me.data?.assistantEnabled ?? false}
        onExtract={applyExtract}
        placeholder="e.g. “Branded USB drive, sells $25, costs $9” — or attach a supplier price list / spec sheet. Each extract fills a row."
      />
      <form onSubmit={submit} style={{ maxWidth: 820 }}>
        {batch.isSuccess && <div className="banner ok">Created {batch.data?.results?.length ?? named.length} item(s) in QuickBooks.</div>}
        {batch.error && <div className="banner error">{(batch.error as Error).message}</div>}

        {rows.map((r, i) => (
          <div className="card" key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>Item {i + 1}</h2>
              {rows.length > 1 && <button type="button" className="link" onClick={() => removeRow(i)}>Remove</button>}
            </div>
            <div className="row cols-2">
              <div className="field"><label>Name *</label><input value={r.name} onChange={(e) => setRow(i, { name: e.target.value })} placeholder="e.g. Legal Consulting" /></div>
              <div className="field"><label>SKU</label><input value={r.sku} onChange={(e) => setRow(i, { sku: e.target.value })} /></div>
            </div>
            <div className="row cols-2">
              <div className="field">
                <label>Type</label>
                <select value={r.type} onChange={(e) => setRow(i, { type: e.target.value })}>
                  <option value="Service">Service</option>
                  <option value="NonInventory">Non-inventory</option>
                </select>
              </div>
              <RefSelect label="Category" value={r.categoryId} onChange={(v) => setRow(i, { categoryId: v })} options={categories.data} placeholder="— none —" />
            </div>
            <div className="row cols-2">
              <div className="field"><label>Sales price</label><input type="number" min="0" step="0.01" value={r.unitPrice} onChange={(e) => setRow(i, { unitPrice: e.target.value })} /></div>
              <RefSelect label="Income account" value={r.incomeAccountId} onChange={(v) => setRow(i, { incomeAccountId: v })} options={income.data} placeholder="Auto (first income account)" />
            </div>
            <div className="row cols-2">
              <div className="field"><label>Cost</label><input type="number" min="0" step="0.01" value={r.purchaseCost} onChange={(e) => setRow(i, { purchaseCost: e.target.value })} /></div>
              <RefSelect label="Expense account" value={r.expenseAccountId} onChange={(v) => setRow(i, { expenseAccountId: v })} options={expense.data} />
            </div>
            <div className="field"><label>Sales description</label><textarea rows={2} value={r.description} onChange={(e) => setRow(i, { description: e.target.value })} /></div>
          </div>
        ))}

        <div className="btn-row">
          <button type="button" onClick={addRow}>+ Add another item</button>
          <button className="primary" type="submit" disabled={!canSubmit}>
            {batch.isPending ? 'Saving…' : `Save ${named.length || ''} item${named.length === 1 ? '' : 's'}`.replace('  ', ' ')}
          </button>
          <button type="button" className="link" onClick={() => { clearDraft(); navigate('/items'); }}>Discard draft</button>
          <span className="muted">Autosaves as a draft. Save creates the item(s) in QuickBooks.{savedAt ? ' · Draft saved' : ''}</span>
        </div>
      </form>
    </>
  );
}
